import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} ProductPurchaseOptionsRefs
 * @property {HTMLElement} [options]
 */

/**
 * Purchase options (one-time + selling plans) for the product form.
 * Morphs on variant change and associates `selling_plan` with the product form.
 *
 * @extends {Component<ProductPurchaseOptionsRefs>}
 */
class ProductPurchaseOptions extends Component {
  /** @type {string} */
  #selectedPlanId = '';

  connectedCallback() {
    super.connectedCallback();
    this.#selectedPlanId = this.#getCheckedValue();
    this.#syncOptionStates();
    this.addEventListener('change', this.#handleChange);
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.name !== 'selling_plan') return;
    this.#selectedPlanId = target.value;
    this.#syncOptionStates();
  };

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newOptions = html.querySelector(
          `product-purchase-options[data-block-id="${this.dataset.blockId}"]`
        );
        if (!newOptions) {
          this.replaceChildren();
          this.hidden = true;
          this.classList.add('product-purchase-options--empty');
          return;
        }

        morph(this, newOptions, { childrenOnly: true });
        this.hidden = newOptions.hasAttribute('hidden');
        this.classList.toggle('product-purchase-options--empty', this.hidden);
        if (!this.hidden) {
          this.#restoreSelection();
          this.#syncOptionStates();
        } else {
          this.#selectedPlanId = '';
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[product-purchase-options] Event promise rejected:', error);
        }
      });
  };

  #getCheckedValue() {
    const checked = this.querySelector('input[name="selling_plan"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : '';
  }

  #restoreSelection() {
    const preferred = this.#selectedPlanId;
    const match = preferred
      ? this.querySelector(`input[name="selling_plan"][value="${CSS.escape(preferred)}"]`)
      : null;

    if (match instanceof HTMLInputElement && !match.disabled) {
      match.checked = true;
      this.#selectedPlanId = match.value;
      return;
    }

    const oneTime = this.querySelector('input[name="selling_plan"][value=""]');
    if (oneTime instanceof HTMLInputElement && !oneTime.disabled) {
      oneTime.checked = true;
      this.#selectedPlanId = '';
      return;
    }

    const firstPlan = this.querySelector('input[name="selling_plan"]:not([value=""])');
    if (firstPlan instanceof HTMLInputElement) {
      firstPlan.checked = true;
      this.#selectedPlanId = firstPlan.value;
    }
  }

  #syncOptionStates() {
    for (const option of this.querySelectorAll('[data-purchase-option]')) {
      if (!(option instanceof HTMLElement)) continue;
      const input = option.querySelector('input[name="selling_plan"]');
      const selected = input instanceof HTMLInputElement && input.checked;
      option.dataset.selected = selected ? 'true' : 'false';
      option.classList.toggle('is-selected', selected);
    }
  }
}

if (!customElements.get('product-purchase-options')) {
  customElements.define('product-purchase-options', ProductPurchaseOptions);
}
